"use client";

import "./header.css";
import Link from "next/link";
import Image from "next/image";
import { FiSend } from "react-icons/fi";
import { GiNightSleep } from "react-icons/gi";
import { usePathname } from "next/navigation";
import useTheme from "src/context/appThemeContext";
import { useState, useEffect, useRef } from "react";
import { MdOutlineLightMode } from "react-icons/md";
import PrimaryButton from "src/components/atoms/PrimaryButton";
import { ABOUTUS_DROPDOWN, NAV_LINKS, SERVICES_DROPDOWN } from "src/utils/mock/headerData";

export default function AppHeader() {
  const { theme, toggleTheme, mounted } = useTheme();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const [mobileServiceOpen, setMobileServiceOpen] = useState(null);
  const [activeLink, setActiveLink] = useState("");
  const dropTimer = useRef(null);
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const openDesktopDrop = (label) => {
    clearTimeout(dropTimer.current);
    setActiveDropdown(label);
  };

  const closeDesktopDrop = () => {
    dropTimer.current = setTimeout(() => setActiveDropdown(null), 130);
  };

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileDropdown(null);
    setMobileServiceOpen(null);
  };

  const toggleMobileDropdown = (key) => {
    setMobileDropdown((prev) => (prev === key ? null : key));
  };

  const toggleMobileService = (idx) => {
    setMobileServiceOpen((prev) => (prev === idx ? null : idx));
  };

  return (
    <>
      <nav className={`nav-shell ${scrolled ? "scrolled" : ""}`}>
        <div className="nav-inner">
          <Link
            href="/"
            onClick={(e) => {
              if (pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
                closeMobile();
              }
            }}
            style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
          >
            <Image src="/images/Tycho_Logo_new.png" alt="Tycho Technologies" width={60} height={10} style={{ objectFit: "contain" }} priority />
            <h3 className="font-display text-2xl md:text-xl xl:text-2xl leading-[1.20] text-text-primary flex flex-col tracking-widest tracking-[4px]">
              TYCHO
              <span className="bg-gradient-to-r from-[var(--c-brand)] to-[var(--c-accent)] bg-clip-text text-transparent md:text-[10px] xl:text-[12px] text-[12px] tracking-[2px]">
                Technologies
              </span>
            </h3>
          </Link>

          <div className="desktop-links">
            {NAV_LINKS.map((link) =>
              link.hasDropdown ? (
                <div key={link.label} className="drop-wrap" onMouseEnter={() => openDesktopDrop(link.dropdown)} onMouseLeave={closeDesktopDrop}>
                  <button className={`nav-link ${activeDropdown === link.dropdown ? "active" : ""}`}>
                    {link.label}
                    <svg className={`chevron ${activeDropdown === link.dropdown ? "open" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {activeDropdown === "services" && link.dropdown === "services" && (
                    <div className="mega-services-panel">
                      <div className="mega-cols modern-scroll">
                        {SERVICES_DROPDOWN.map((service) => (
                          <div key={service.label} className="mega-col">
                            <Link href={service.href} className="mega-col-header" onClick={() => setActiveDropdown(null)}>
                              <span className="mega-col-icon">{service.icon}</span>
                              <span className="mega-col-label">{service.label}</span>
                            </Link>

                            <ul className="mega-sublist">
                              {service.subLinks.map((sub) => (
                                <li key={sub.label}>
                                  <Link href={sub.href} className="mega-sublink" onClick={() => setActiveDropdown(null)}>
                                    <span className="mega-sublink-icon">{sub.icon}</span>
                                    {sub.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeDropdown === "about" && link.dropdown === "about" && (
                    <div className="mega-about-panel">
                      <div className="mega-about-list">
                        {ABOUTUS_DROPDOWN.map((item) => (
                          <Link key={item.label} href={item.href} className="mega-about-item" onClick={() => setActiveDropdown(null)}>
                            <span className="mega-about-icon">{item.icon}</span>
                            <span className="mega-about-body">
                              <span className="mega-about-label">{item.label}</span>
                            </span>
                            <svg className="mega-about-arrow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </Link>
                        ))}
                      </div>

                      <div className="mega-stats">
                        {[
                          { val: "300+", label: "Projects" },
                          { val: "50+", label: "Clients" },
                          { val: "10+", label: "Years" },
                        ].map((s) => (
                          <div key={s.label} className="mega-stat">
                            <span className="mega-stat-val">{s.val}</span>
                            <span className="mega-stat-label">{s.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link key={link.label} href={link.href} className={`nav-link ${activeLink === link.href ? "active" : ""}`} onClick={() => setActiveLink(link.href)}>
                  {link.label}
                </Link>
              ),
            )}
          </div>

          <div className="right-side">
            <button className="theme-btn" onClick={toggleTheme} aria-label="Toggle theme" disabled={!mounted}>
              {mounted ? theme === "dark" ? <GiNightSleep color="white" /> : <MdOutlineLightMode /> : <GiNightSleep color="white" />}
            </button>

            <PrimaryButton size="lg" href="/contact-us" icon={<FiSend />} className="px-7! py-2! xl:px-5! md:px-3! cta-btn">
              Contact Us
            </PrimaryButton>

            <button className="hamburger" onClick={() => setMobileOpen((v) => !v)} aria-label="Toggle menu" aria-expanded={mobileOpen}>
              <span className="ham-bar" style={mobileOpen ? { transform: "rotate(45deg) translate(5px,5px)" } : {}} />
              <span className="ham-bar" style={mobileOpen ? { opacity: 0, transform: "scaleX(0)" } : {}} />
              <span className="ham-bar" style={mobileOpen ? { transform: "rotate(-45deg) translate(5px,-5px)" } : {}} />
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="mobile-drawer">
            <div className="drawer-inner">
              {NAV_LINKS.map((link) =>
                link.hasDropdown ? (
                  <div key={link.label}>
                    <button
                      className={`mob-acc-header ${mobileDropdown === link.dropdown ? "open" : ""}`}
                      onClick={() => toggleMobileDropdown(link.dropdown)}
                      aria-expanded={mobileDropdown === link.dropdown}
                    >
                      {link.label}
                      <svg
                        style={{
                          width: 14,
                          height: 14,
                          transition: "transform .25s",
                          transform: mobileDropdown === link.dropdown ? "rotate(180deg)" : "rotate(0deg)",
                          flexShrink: 0,
                        }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {link.dropdown === "services" && (
                      <div className={`mob-submenu ${mobileDropdown === "services" ? "open" : ""}`} style={{ maxHeight: mobileDropdown === "services" ? "1200px" : "0" }}>
                        <div className="mob-submenu-inner">
                          {SERVICES_DROPDOWN.map((service, idx) => (
                            <div key={service.label}>
                              <button className="mob-service-acc" onClick={() => toggleMobileService(idx)}>
                                <span className="mob-service-acc-left">
                                  <span className="mob-service-icon">{service.icon}</span>
                                  <Link
                                    href={service.href}
                                    className="mob-service-label"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      closeMobile();
                                    }}
                                  >
                                    {service.label}
                                  </Link>
                                </span>
                                <svg
                                  style={{
                                    width: 12,
                                    height: 12,
                                    transition: "transform .2s",
                                    flexShrink: 0,
                                    transform: mobileServiceOpen === idx ? "rotate(180deg)" : "rotate(0deg)",
                                  }}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>

                              <div className={`mob-service-sublist ${mobileServiceOpen === idx ? "open" : ""}`}>
                                {service.subLinks.map((sub) => (
                                  <Link key={sub.label} href={sub.href} className="mob-sub-link" onClick={closeMobile}>
                                    <span>{sub.icon}</span>
                                    <span>{sub.label}</span>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {link.dropdown === "about" && (
                      <div className={`mob-submenu ${mobileDropdown === "about" ? "open" : ""}`}>
                        <div className="mob-submenu-inner">
                          {ABOUTUS_DROPDOWN.map((item) => (
                            <Link key={item.label} href={item.href} className="mob-sub-link" onClick={closeMobile}>
                              <span>{item.icon}</span>
                              <span>
                                <span style={{ display: "block", fontWeight: 600, color: "var(--c-ink-2)", fontFamily: "var(--font-display, sans-serif)", fontSize: 13 }}>
                                  {item.label}
                                </span>
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link key={link.label} href={link.href} className="mob-link" onClick={closeMobile}>
                    {link.label}
                  </Link>
                ),
              )}

              <div className="drawer-footer">
                <PrimaryButton size="lg" href="/contact-us" icon={<FiSend />} className="py-2! px-5!">
                  Contact Us
                </PrimaryButton>
              </div>
            </div>
          </div>
        )}
      </nav>

      <div style={{ height: "66px" }} />
    </>
  );
}
